import { NavLink, useNavigate } from "@remix-run/react";
import { Input } from "./ui/input";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "./ui/navigation-menu";
import { useStoreContext } from "~/hooks/use-store";
import {
  formatPrice,
  sellingPrice,
  slugify,
} from "~/lib/helpers/storehelpers";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Package, Search } from "lucide-react";

const navLinkClass = (
  { isActive, isPending }: { isActive: boolean; isPending: boolean },
) =>
  `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
    isActive
      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
      : isPending
      ? "text-gray-400"
      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
  }`;

export default function StoreHeader() {
  const { storeDetails, storeProducts } = useStoreContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  if (!storeDetails) {
    return <div>Store not found</div>;
  }

  const storeSlug = slugify(storeDetails.name);

  const matches = searchTerm.trim()
    ? storeProducts.filter((p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 6)
    : [];

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (selectedIndex >= 0 && matches[selectedIndex]) {
      navigateToProduct(matches[selectedIndex].id);
    } else if (searchTerm.trim()) {
      navigate(
        `/store/${storeSlug}/search?searchTerm=${
          encodeURIComponent(searchTerm.trim())
        }`,
      );
      setIsOpen(false);
    }
  };

  const navigateToProduct = (productId: string) => {
    setSearchTerm("");
    setIsOpen(false);
    setSelectedIndex(-1);
    navigate(`/store/${storeSlug}/products?product=${productId}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalMatches = searchTerm.trim()
    ? storeProducts.filter((p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ).length
    : 0;

  return (
    <div
      className="border-b bg-white dark:bg-gray-900 shadow-sm"
      ref={wrapperRef}
    >
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="flex items-center justify-between gap-3 py-3">
          <NavigationMenu>
            <NavigationMenuList className="flex gap-1">
              <NavigationMenuItem>
                <NavLink
                  className={navLinkClass}
                  to={`/store/${storeSlug}/home`}
                >
                  Home
                </NavLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavLink
                  className={navLinkClass}
                  to={`/store/${storeSlug}/products`}
                >
                  Products
                </NavLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavLink
                  className={navLinkClass}
                  to={`/store/${storeSlug}/reviews`}
                >
                  Reviews
                </NavLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <form
            onSubmit={handleSearchSubmit}
            className="w-full max-w-[180px] sm:max-w-xs md:max-w-md"
          >
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                className="w-full h-9 text-sm pl-8"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedIndex(-1);
                  if (e.target.value.trim()) setIsOpen(true);
                  else setIsOpen(false);
                }}
                onFocus={() => {
                  if (searchTerm.trim()) setIsOpen(true);
                }}
                onKeyDown={handleKeyDown}
              />
            </div>
          </form>
        </div>

        {isOpen && matches.length > 0 && (
          <div className="absolute left-4 right-4 md:left-6 md:right-6 top-full mt-1 rounded-lg border border-gray-200 bg-white shadow-lg z-50 max-h-72 overflow-y-auto">
            {matches.map((product, i) => (
              <button
                key={product.id}
                type="button"
                onClick={() => navigateToProduct(product.id)}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  selectedIndex === i ? "bg-orange-50" : "hover:bg-gray-50"
                }`}
              >
                <Package className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {product.name}
                  </p>
                </div>
                {(() => {
                  const s = sellingPrice(product.price, product.markdown);
                  if (s === null || s <= 0) return null;
                  const md = product.markdown;
                  const pr = product.price;
                  const showSale =
                    md != null &&
                    pr != null &&
                    md > 0 &&
                    pr > md;
                  if (showSale) {
                    return (
                      <span className="flex flex-shrink-0 items-baseline gap-1.5 text-right">
                        <span className="text-sm font-semibold text-gray-900">
                          ${formatPrice(md)}
                        </span>
                        <span className="text-xs text-gray-500 line-through">
                          ${formatPrice(pr)}
                        </span>
                      </span>
                    );
                  }
                  return (
                    <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
                      ${formatPrice(s)}
                    </span>
                  );
                })()}
              </button>
            ))}
            {totalMatches > 6 && (
              <button
                type="button"
                onClick={() => {
                  navigate(
                    `/store/${storeSlug}/search?searchTerm=${
                      encodeURIComponent(searchTerm.trim())
                    }`,
                  );
                  setIsOpen(false);
                }}
                className="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 px-3 py-2.5 text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors"
              >
                View all results
              </button>
            )}
          </div>
        )}

        {isOpen && searchTerm.trim() && matches.length === 0 && (
          <div className="absolute left-4 right-4 md:left-6 md:right-6 top-full mt-1 rounded-lg border border-gray-200 bg-white shadow-lg z-50 px-3 py-4 text-center text-sm text-gray-500">
            No products match &ldquo;{searchTerm.trim()}&rdquo;
          </div>
        )}
      </div>
    </div>
  );
}
